/**
 * API Service
 *
 * Base API client for Vibe Designer backend.
 */

import { authService } from './authService';

const API_BASE_URL = '/api';

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown
  ) {
    super(`API Error: ${status} ${statusText}`);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

/**
 * E.21.1 (2026-04-30) stale-token handler. On 401 with a token in
 * localStorage, clear the token + user + redirect to /login. Catches both
 * the app-mount stale-token scenario (NH.E21 manual MCP browser audit) AND
 * any mid-session token expiry. The `throw` afterwards is the PRIMARY
 * mechanism preventing post-401 setState on unmounted components — the
 * navigation fires in the next macrotask, not synchronously.
 */
function handle401StaleToken(): void {
  if (localStorage.getItem('vibe_designer_token')) {
    // clearStoredAuth removes the refresh token too — on this terminal path
    // (refresh failed or the retry still 401'd) a leftover refresh token would
    // cost one wasted renewal round-trip on the next load. 2026-06-25.
    authService.clearStoredAuth();
    window.location.replace('/login');
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    if (response.status === 401) {
      handle401StaleToken();
    }
    // E.6 (2026-04-29): clone the response BEFORE consuming the body via
    // `.json()`. If JSON parse fails, fall back to `.text()` on the clone.
    // Without this, the body stream is locked after `.json()` and `.text()`
    // throws "Body is unusable" — the original parse error gets swallowed.
    const cloned = response.clone();
    let data: unknown;
    try {
      data = await response.json();
    } catch {
      data = await cloned.text();
    }
    throw new ApiError(response.status, response.statusText, data);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

function buildUrl(path: string, params?: Record<string, string | number | boolean | undefined>): string {
  const url = new URL(`${API_BASE_URL}${path}`, window.location.origin);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  return url.toString();
}

/**
 * Z.2: exported so callers issuing bare `fetch()` for non-JSON responses
 * (e.g., binary blobs from /api/voice/tts) can attach the same Bearer
 * token. Single source of truth for the localStorage key. Same-origin
 * cookie + JWT both flow; AuthMiddleware on the backend requires this.
 */
export function getStoredToken(): string | null {
  return localStorage.getItem('vibe_designer_token');
}

export function getAuthHeaders(): Record<string, string> {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * D1-1/D1-3/D1-5 (2026-06-09 audit) — canonical first-frame auth payload for
 * ALL authenticated WebSockets (voice, /ws, ttyd-proxy). Browsers cannot set
 * Authorization headers on a WS upgrade, and the Mode-1 VPS edge has no
 * auth_request, so the JWT travels in the FIRST WS frame (not a `?token=`
 * query param, which nginx would write to access.log). Returns null when no
 * token is stored (local dev) — callers send no auth frame and the backend
 * gate is AUTH_ENABLED=false there. Matches the backend
 * middleware.auth.consume_ws_auth_frame shape `{type:"auth", token:<jwt>}`.
 */
export function wsAuthFrame(): { type: 'auth'; token: string } | null {
  const token = getStoredToken();
  return token ? { type: 'auth', token } : null;
}

/**
 * Back-compat alias — voice WS callers (voiceStore.ts) import this name. The
 * frame shape is WS-auth-generic, not voice-specific; new callers should use
 * wsAuthFrame(). Kept as a thin delegate so there is one source of truth.
 */
export function voiceAuthFrame(): { type: 'auth'; token: string } | null {
  return wsAuthFrame();
}

/**
 * Plan E Phase 2: derive the per-tenant Pi shell URL for the Editor's
 * Terminal tab iframe. LAN-only v1 — VPS access is OF1.
 */
export function piShellUrl(tenantId: string): string {
  return `http://pi-${tenantId}.t440.gamemagick.internal/`;
}

/**
 * Plan E Phase 2: LAN-only gate. The Terminal tab iframe approach relies on
 * Authentik cookie inheritance from `.t440.gamemagick.internal` — VPS hosts
 * (`*.gamemagick.com`) don't share that cookie domain. Hide the tab there.
 * Localhost permitted for dev + jsdom test runs.
 *
 * Canonical LAN hostname pattern: `vibe-{tenant}.t440.gamemagick.internal`
 * (verified against all 6 vibe-designer compose files; no aliases).
 */
export function isLanContext(): boolean {
  const h = window.location.hostname;
  return h.endsWith('.t440.gamemagick.internal') || h === 'localhost' || h === '127.0.0.1';
}

/**
 * Fetch with a one-shot silent-refresh retry (2026-06-25). On a 401 (when a
 * token is present), attempt a single-flight token refresh; on success re-issue
 * the request ONCE with a freshly-read Bearer header (overriding the stale one).
 * On failure — or no token — the original 401 Response is returned, and the
 * caller's handleResponse / stream guard runs handle401StaleToken (clear +
 * /login). This is the reactive safety net behind the proactive scheduler.
 */
async function fetchWithAuthRetry(url: string, init: RequestInit): Promise<Response> {
  const resp = await fetch(url, init);
  if (resp.status !== 401 || getStoredToken() === null) return resp;
  const refreshed = await authService.refreshAccessToken();
  if (!refreshed) return resp;
  return fetch(url, {
    ...init,
    headers: { ...(init.headers as Record<string, string> | undefined), ...getAuthHeaders() },
  });
}

export const api = {
  async get<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options;
    const response = await fetchWithAuthRetry(buildUrl(path, params), {
      ...fetchOptions,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
        ...fetchOptions.headers,
      },
    });
    return handleResponse<T>(response);
  },

  async post<T>(path: string, data?: unknown, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options;
    const response = await fetchWithAuthRetry(buildUrl(path, params), {
      ...fetchOptions,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
        ...fetchOptions.headers,
      },
      // `data !== undefined` (not truthy) so falsy-but-meaningful bodies like
      // `{flag: false}` or `{value: 0}` serialize cleanly. Truthy check would
      // silently elide them. Matches put + patch (Y.3 reviewer C1).
      body: data !== undefined ? JSON.stringify(data) : undefined,
    });
    return handleResponse<T>(response);
  },

  async put<T>(path: string, data?: unknown, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options;
    const response = await fetchWithAuthRetry(buildUrl(path, params), {
      ...fetchOptions,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
        ...fetchOptions.headers,
      },
      body: data !== undefined ? JSON.stringify(data) : undefined,
    });
    return handleResponse<T>(response);
  },

  async delete<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options;
    const response = await fetchWithAuthRetry(buildUrl(path, params), {
      ...fetchOptions,
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
        ...fetchOptions.headers,
      },
    });
    return handleResponse<T>(response);
  },

  async patch<T>(path: string, data?: unknown, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options;
    const response = await fetchWithAuthRetry(buildUrl(path, params), {
      ...fetchOptions,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
        ...fetchOptions.headers,
      },
      // Y.3 — `data` is the request body object; we explicitly stringify even
      // when fields are null (e.g. {display_name: null} to clear an overlay).
      // `undefined` data sends no body. Object literals are truthy, so the
      // null-field case correctly falls through to JSON.stringify.
      body: data !== undefined ? JSON.stringify(data) : undefined,
    });
    return handleResponse<T>(response);
  },

  /**
   * POST multipart/form-data (2026-06-28). For file uploads (e.g. PDF ingest).
   * Deliberately omits the Content-Type header so the browser sets the
   * multipart boundary; Bearer + the one-shot 401 refresh-retry + handleResponse
   * are identical to api.post. This is the sanctioned auth-attached path for
   * binary/blob bodies (the bare-fetch the no-restricted-syntax rule forbids
   * would omit the Bearer header — this does not).
   */
  async postForm<T>(path: string, form: FormData, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options;
    const response = await fetchWithAuthRetry(buildUrl(path, params), {
      ...fetchOptions,
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        ...fetchOptions.headers,
      },
      body: form,
    });
    return handleResponse<T>(response);
  },

  /**
   * Stream a POST request using Server-Sent Events pattern.
   */
  async *stream<T>(
    path: string,
    data?: unknown,
    options: RequestOptions = {}
  ): AsyncGenerator<T, void, unknown> {
    const { params, ...fetchOptions } = options;
    const response = await fetchWithAuthRetry(buildUrl(path, params), {
      ...fetchOptions,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...getAuthHeaders(),
        ...fetchOptions.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
      // E.21.1 (2026-04-30) NH.E21.1.G0 F1: stream() bypasses handleResponse,
      // so re-apply the 401 stale-token interceptor here. Without this, a
      // streamed call (codeActionsPlugin "Refactor with AI" via E.17 wire-up)
      // hitting a stale-token 401 would silently leave the user in the broken
      // workspace instead of redirecting to /login.
      if (response.status === 401) {
        handle401StaleToken();
      }
      throw new ApiError(response.status, response.statusText);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Response body is not readable');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              return;
            }
            try {
              yield JSON.parse(data) as T;
            } catch {
              // Skip invalid JSON
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },
};

export default api;
